import * as React from 'react';
import injectSheet, { Styles, WithSheet } from 'react-jss';
import scrollIntoView from 'scroll-into-view-if-needed';

import { BaseComponent } from '../../BaseComponent';
import { LayoutType } from '../../../constants/LayoutConstants';
import { ViewSettingsStore } from '../../../stores/ViewSettingsStore';
import { UIHelper } from '../../../utils/UIHelper';
import {
    primaryTextColor, backgroundDefault, borderColor, backgroundHover,
    MediaQueryDesktop, MediaQuerySmallPortrait
} from '../../../layout/BaseLayout';
import { AppConfigurationStore } from '../../../stores/AppConfigurationStore';
import { PersistentStore, SidebarPosition } from '../../../stores/PersistentStore';

interface CatalogLetterBarState {
    locked: boolean;
    sidebarPosition: SidebarPosition;
}

interface CatalogLetterBarProps {
    layoutType: LayoutType;
    scrollCallback: (scrollTop: number) => void;
    inSide: boolean;
}

export class CatalogLetterBar extends BaseComponent<WithSheet<CatalogLetterBarClasses, {}> & CatalogLetterBarProps, CatalogLetterBarState> {
    private static LETTERS: Array<string> = [
        'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H',
        'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P',
        'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X',
        'Y', 'Z',
        '#', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
    ];

    public componentWillMount(): void {
        this.addStoreListener(ViewSettingsStore, this.onChange);
        this.addStoreListener(PersistentStore, this.onChange);
        this.onChange();
    }

    public render(): JSX.Element {
        return (
            <div className={this.className()}>
                <div className={this.props.classes.buttons}>
                    {this.state.locked ? <div className="locked-letter-buttons"/> : null}
                    {CatalogLetterBar.LETTERS.map((letter: string, index: number) => {
                        if (index > 26) {
                            return null;
                        }

                        return (
                            // Children of letter-button must have css-property pointer-events: none
                            <div
                                key={index}
                                className={this.props.classes.button}
                                onClick={this.onClickOrTouch(index)}
                                onTouchStart={this.onClickOrTouch(index)}
                                onMouseMove={this.onMouseMove(index)}
                                onTouchMove={this.onTouchMove}
                            >
                                <div className={this.props.classes.letter}>{letter}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    protected className = (): string => {
        const classNames: Array<string> = [this.props.classes.root];
        if (this.props.inSide) {
            classNames.push(`${this.props.classes.root}-inside`);
        }
        if (AppConfigurationStore.getState().inApp) {
            classNames.push(`${this.props.classes.root}-app`);
        }
        if (this.state.sidebarPosition === 'right') {
            classNames.push(`${this.props.classes.root}-right`);
        }
        return classNames.join(' ');
    };

    protected onChange = (): void => {
        this.setState({
            locked: ViewSettingsStore.getState().sidebarlocked,
            sidebarPosition: PersistentStore.getState().sidebarPosition
        });
    };

    protected onTouchMove = (event: React.TouchEvent) => {
        if (AppConfigurationStore.getState().iDevice) {
            // because in iOS Safari touchmove also scrolls the catalog in background the catalog would just flicker
            return;
        }
        UIHelper.stopEvent(event);
        const myTouch: React.Touch = event.changedTouches[0];
        if (myTouch != null && myTouch.clientX != null && myTouch.clientY != null) {
            const element: HTMLElement = document.elementFromPoint(myTouch.clientX, myTouch.clientY) as HTMLElement;
            if (element && element.click && element.classList.contains(this.props.classes.button)) {
                element.click();
            }
        }
    };

    protected onMouseMove = (letterIndex: number) => (event: React.MouseEvent) => {
        UIHelper.stopEvent(event);
        if (event.buttons != null && event.buttons === 1) {
            this.scrollTo(letterIndex);
        }
    };

    protected onClickOrTouch = (letterIndex: number) => (event: React.MouseEvent | React.TouchEvent) => {
        UIHelper.stopEvent(event);
        this.scrollTo(letterIndex);
    };

    protected scrollTo(letterIndex: number): void {
        let scrolled: boolean = false;
        let heightFix: number = 10;
        if (!AppConfigurationStore.getState().iDevice && !AppConfigurationStore.getState().inApp) {
            switch (this.props.layoutType) {
                case LayoutType.DESKTOP:
                    heightFix = 106;
                    break;
                case LayoutType.TABLET:
                    heightFix = 84;
                    break;
                case LayoutType.SMALL:
                    heightFix = 103;
                    break;
                default:
                    break;
            }
        }

        CatalogLetterBar.LETTERS.forEach((letter: string, index: number) => {
            if (
                scrolled ||
                index < letterIndex ||
                letter === '#'
            ) {
                return;
            }
            const letterAnchor: HTMLElement | null = document.getElementById(`${letter.toLocaleLowerCase()}-catalogitem`);
            if (letterAnchor == null) {
                return;
            }
            if (AppConfigurationStore.getState().inApp) {
                scrollIntoView(letterAnchor, {behavior: 'smooth', block: 'start'});
            } else {
                this.props.scrollCallback(letterAnchor.offsetTop - heightFix);
            }
            scrolled = true;
        });
    }
}

type CatalogLetterBarClasses = 'root' | 'buttons' | 'button' | 'letter';

const styles: Styles<CatalogLetterBarClasses> = {
    root: {
        display: 'flex',
        alignItems: 'center',
        bottom: 0,
        left: 'calc(100% + 1px)',
        opacity: 1,
        overflow: 'hidden',
        position: 'absolute',
        textAlign: 'center',
        top: 0,
        width: 27,
        zIndex: 6,
        '&&-inside': {
            left: 'auto',
            right: 0,
            width: 32,
            '& $buttons': {
                background: 'transparent',
                border: 0,
                height: '100%'
            },
            '& $button': {
                minHeight: 10,
                maxHeight: 20,
                paddingLeft: 16,
            },
            '& $letter': {
                lineHeight: '12px'
            }
        },
        '&&-right:not(&-inside)': {
            left: 'auto',
            right: 'calc(100% + 1px)',
            '& $buttons': {
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 0,
                borderLeft: `1px solid ${borderColor}`,
                borderRight: 0,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 0
            }
        },
        '&-app': {
            padding: '57px 0 0'
        },
        '&:not(&-app)': {
            [MediaQuerySmallPortrait]: {
                right: 9
            }
        }
    },
    buttons: {
        display: 'flex',
        alignItems: 'center',
        background: backgroundDefault,
        borderBottom: `1px solid ${borderColor}`,
        borderBottomRightRadius: 4,
        borderRight: `1px solid ${borderColor}`,
        borderTop: `1px solid ${borderColor}`,
        borderTopRightRadius: 4,
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '7px 0',
        position: 'relative',
        width: '100%'
    },
    button: {
        display: 'flex',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1,
        alignItems: 'center',
        cursor: 'pointer',
        fontSize: 10,
        lineHeight: '18px',
        minHeight: 18,
        width: '100%',
        [MediaQueryDesktop]: {
            lineHeight: '20px',
            minHeight: 20
        },
        'core-hover-enabled &:hover': {
            color: primaryTextColor
        },
        '&::active': {
            background: `${backgroundHover} content-box`,
            color: primaryTextColor
        },
    },
    letter: {
        display: 'block',
        pointerEvents: 'none',
        width: '100%'
    },
};

export default injectSheet(styles)(CatalogLetterBar);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/sidebar/catalog/CatalogLetterBar.tsx